import { useEffect, useRef, useState } from "react";
import { fetchPrivateImage } from "@/lib/api";

// Module-level LRU cache of blob URLs so re-renders / re-visits don't refetch.
const _blobCache = new Map();
const _CACHE_MAX = 120;
function cachePut(key, url) {
  if (_blobCache.has(key)) _blobCache.delete(key);
  _blobCache.set(key, url);
  while (_blobCache.size > _CACHE_MAX) {
    const [oldK, oldV] = _blobCache.entries().next().value;
    try { URL.revokeObjectURL(oldV); } catch (e) { /* noop */ }
    _blobCache.delete(oldK);
  }
}

/**
 * AuthImg — private image loader with:
 *  - IntersectionObserver lazy loading (only fetches when near viewport)
 *  - Optional server-side thumbnail via ?w=  (much smaller download)
 *  - In-memory blob URL cache across mounts
 */
export function AuthImg({ path, alt = "", className = "", width, priority = false }) {
  const [src, setSrc] = useState(null);
  const [visible, setVisible] = useState(priority);
  const ref = useRef(null);

  useEffect(() => {
    if (priority) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { setVisible(true); io.disconnect(); break; }
      }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [priority]);

  useEffect(() => {
    if (!path || !visible) return;
    const key = `${path}|${width || 0}`;
    const cached = _blobCache.get(key);
    if (cached) { setSrc(cached); return; }
    let cancelled = false;
    (async () => {
      const url = await fetchPrivateImage(path, width);
      if (cancelled) return;
      if (url) { cachePut(key, url); setSrc(url); }
    })();
    return () => { cancelled = true; };
  }, [path, visible, width]);

  if (!src) {
    return (
      <div ref={ref} className={`${className} ${visible ? "hl-sweep" : "bg-[#171717]"} border border-[#262626] flex items-center justify-center text-neutral-600 text-xs`}>
        {visible ? <span className="sr-only">Cargando imagen</span> : "Sin imagen"}
      </div>
    );
  }
  return <img ref={ref} src={src} alt={alt} className={className} loading="lazy" />;
}
